import { decode, encode } from "./protocol";
import type { Transport } from "./transport/transport";

/** Замер связи до раунда: медиана и разброс пинга, мс. */
export interface LinkQuality {
  readonly ping: number;
  readonly jitter: number;
  readonly samples: number;
}

/** Кадр мира, мс. */
const FRAME_MS = 1000 / 60;

/**
 * Прогрев связи (консультант 04.11.2026: «сначала лагает, потом терпимо»): обе стороны durationMs шлют друг
 * другу пинг каждые 100 мс и отвечают на чужой. Раунд начинается после замера, а задержка ввода выбирается
 * по измеренному пингу, а не догоняет в бою. Отвечает на пинги и после замера, пока не придёт первый бой.
 */
export function warmup(
  transport: Transport,
  durationMs = 2500,
  now: () => number = () => performance.now(),
): Promise<LinkQuality> {
  return new Promise((resolve) => {
    const rtts: number[] = [];
    transport.onMessage((bytes) => {
      const message = decode(bytes);
      if (message?.type === "ping") transport.send(encode({ type: "pong", stamp: message.stamp }));
      else if (message?.type === "pong") rtts.push((now() - message.stamp + 2 ** 32) % 2 ** 32);
    });
    const tick = setInterval(() => {
      transport.send(encode({ type: "ping", stamp: Math.floor(now()) >>> 0 }));
    }, 100);
    setTimeout(() => {
      clearInterval(tick);
      const sorted = [...rtts].sort((a, b) => a - b);
      const at = (share: number): number => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * share))] ?? 0;
      resolve({ ping: Math.round(at(0.5)), jitter: Math.round(at(0.9) - at(0.1)), samples: sorted.length });
    }, durationMs);
  });
}

/**
 * Своя задержка ввода по замеру: половина пинга плюс разброс — столько кадров, но 1–4. Остальное покрывает откат
 * (до 8 кадров). Плохой связи (нет ответов) — 3 кадра.
 */
export function inputDelayFor(link: LinkQuality): number {
  if (link.samples === 0) return 3;
  const frames = Math.ceil((link.ping / 2 + link.jitter / 2) / FRAME_MS);
  return Math.max(1, Math.min(4, frames));
}
